/**
 * @fileoverview Shared Mapbox GL layer definitions for the fire hazard
 *               zone overlay and the active-fire perimeter overlay
 *               (Phase 5.9). This is the SINGLE canonical implementation,
 *               consumed identically by mobile-pwa, tenant-web, and
 *               system-web via their own thin `fireHazardLayers.ts`
 *               re-export shims (`export * from
 *               '@firesquirrel/fire-hazard-map-layers'`) — previously each
 *               app carried its own byte-for-byte-identical copy of this
 *               logic, which this package replaces.
 * @module fire-hazard-map-layers/fireHazardLayers
 *
 * Responsibilities:
 * - Define the Mapbox source and fill/outline layers for fire hazard
 *   zones, with data-driven styling by hazard level
 *   (low/medium/high/extreme).
 * - Provide a configurable fill opacity (default from
 *   FIRE_HAZARD_OVERLAY_OPACITY.DEFAULT_FILL) so whatever the host app
 *   renders on top (parcel colors, boundary highlights) remains visible.
 * - Define the active-fire perimeter layer (outline only, no fill,
 *   pulsing bright red) and a start/stop pulse animation helper.
 * - Provide a WUI-only filter toggle for the hazard zone fill/outline
 *   layers.
 * - Provide add/remove/visibility functions following the established
 *   Mapbox layer pattern (addSource/addLayer, checked removal).
 *
 * Dependencies:
 * - mapbox-gl (type-only import — the map instance is passed in by the
 *   caller; this package has no runtime Mapbox dependency of its own).
 * - @firesquirrel/core-domain — hazard level colors/opacity defaults and
 *   ACTIVE_FIRE_STYLE (color/pulse constants) — the single source of
 *   truth these three apps must never drift on.
 *
 * Architecture:
 * - Pure, host-agnostic Mapbox GL layer manipulation — every function
 *   takes a `mapboxgl.Map` instance and has no dependency on any host
 *   app's routing, state management, or service layer. This is what
 *   makes it safe to share across mobile-pwa/tenant-web/system-web
 *   despite those apps being otherwise-independent Vite builds with no
 *   shared component tree.
 * - The hazard zone overlay is intended to be rendered BELOW
 *   parcel/boundary layers — the HOST app is responsible for calling
 *   addFireHazardLayers() with an optional `beforeId` to ensure it inserts
 *   below its own parcel/boundary layers; this package provides the
 *   `beforeId` parameter to support this ordering (Phase 5.9 Sprint 5).
 *   As of this writing only mobile-pwa passes `beforeId` (tenant-web and
 *   system-web still rely on registration order — they add hazard layers
 *   before their boundary layer on every path, so no `beforeId` is
 *   currently needed there). If `beforeId`'s target doesn't exist yet at
 *   add time (e.g. a style swap where both layers are being recreated and
 *   order isn't guaranteed), call `reorderFireHazardLayersBeforeId()` once
 *   the target layer is confirmed to exist, to re-assert correct z-order.
 * - Fill opacity is kept low (0.15-0.20 range, enforced by
 *   FIRE_HAZARD_OVERLAY_OPACITY from core-domain) so parcel/boundary
 *   context underneath remains clearly visible.
 * - The active-fire "pulsing" effect is implemented as a setInterval loop
 *   that recomputes line-opacity via a sine wave — Mapbox GL has no
 *   native paint-property animation primitive. The CALLER owns the
 *   interval lifecycle (start on mount/visibility-on, stop on
 *   unmount/visibility-off via the returned cleanup function) since this
 *   module has no component lifecycle of its own.
 *
 * Notes:
 * - Hazard level colors follow USFS/CAL FIRE conventions: green (low),
 *   yellow (medium), orange (high), red (extreme).
 * - Layer/source IDs (FIRE_HAZARD_SOURCE_ID, ACTIVE_FIRE_SOURCE_ID,
 *   ACTIVE_FIRE_LAYER_ID, and the internal fill/outline layer ids) are
 *   part of this module's contract with every host app's MapView-style
 *   component — changing them requires updating every caller.
 */

import type mapboxgl from 'mapbox-gl';
import {
  type FireHazardLevel,
  FIRE_HAZARD_FILL_COLORS,
  FIRE_HAZARD_OUTLINE_COLORS,
  FIRE_HAZARD_OVERLAY_OPACITY,
  ACTIVE_FIRE_STYLE,
} from '@firesquirrel/core-domain';

export const FIRE_HAZARD_SOURCE_ID = 'fire-hazard-source';
export const ACTIVE_FIRE_SOURCE_ID = 'active-fire-source';
export const ACTIVE_FIRE_LAYER_ID = 'fire-hazard-active-fire';

/**
 * Global feature flag to hide/show all fire-hazard related UI menus and
 * toggles across all three web apps (Phase 5.9 Sprint 5).
 * Currently set to FALSE per customer request; flipping to true restores
 * all map toggles and menus.
 */
export const FIRE_HAZARD_UI_ENABLED = false;

/** Layer IDs managed by this module. */
const FIRE_HAZARD_LAYER_IDS = ['fire-hazard-fill', 'fire-hazard-outline'] as const;

/**
 * Default fill/outline opacity — low enough that whatever the host app
 * renders underneath (parcel colors, boundary highlights) shows through.
 * Sourced from @firesquirrel/core-domain so every host app enforces the
 * same "must never fully obscure" bounds from a single place.
 */
export const DEFAULT_FIRE_HAZARD_OPACITY = FIRE_HAZARD_OVERLAY_OPACITY.DEFAULT_FILL;
export const DEFAULT_FIRE_HAZARD_OUTLINE_OPACITY = FIRE_HAZARD_OVERLAY_OPACITY.DEFAULT_OUTLINE;

/**
 * @contract FireHazardFeature
 * Purpose: Properties on each GeoJSON feature in the fire hazard source.
 * Fields:  hazardLevel — severity classification (low/medium/high/extreme).
 *          source — data source identifier (e.g. 'USFS', 'CAL_FIRE', 'NIFC').
 *          zone — zone name or code from the source dataset.
 *          inWUI — whether the zone is in the Wildland-Urban Interface.
 * Rules:   hazardLevel drives the fill color via HAZARD_COLORS mapping.
 * Sync:    Not persisted in Dexie in any host app — data comes from each
 *          host's own online-only overlay fetch (see that app's
 *          fireHazardOverlayService.ts).
 */
export interface FireHazardFeature {
  hazardLevel: FireHazardLevel;
  source?: string;
  zone?: string;
  inWUI?: boolean;
}

/** Re-export colors from core-domain for backward-compatible naming. */
export const HAZARD_COLORS = FIRE_HAZARD_FILL_COLORS;
export const HAZARD_OUTLINE_COLORS = FIRE_HAZARD_OUTLINE_COLORS;

/**
 * @process addFireHazardLayers
 *
 * Steps:
 * 1. Add a fill layer with data-driven color by hazardLevel.
 * 2. Add an outline layer with data-driven line color by hazardLevel.
 *
 * Inputs:
 * - map: The Mapbox GL map instance. Its FIRE_HAZARD_SOURCE_ID source
 *   must already exist (the host app adds it before calling this).
 * - beforeId: Optional layer ID to insert before.
 *
 * Outputs:
 * - Layers registered on the map (no return value).
 *
 * Failure Handling:
 * - Mapbox addLayer throws if a layer with the same ID already exists.
 *   Callers should call removeFireHazardLayers() first or check existence.
 *
 * Idempotency:
 * - NOT REQUIRED — calling twice without removing will throw.
 */
export function addFireHazardLayers(map: mapboxgl.Map, beforeId?: string): void {
  // Fill layer — data-driven color by hazard level, low opacity
  map.addLayer({
    id: 'fire-hazard-fill',
    type: 'fill',
    source: FIRE_HAZARD_SOURCE_ID,
    paint: {
      'fill-color': [
        'match',
        ['get', 'hazardLevel'],
        'low', HAZARD_COLORS.low,
        'medium', HAZARD_COLORS.medium,
        'high', HAZARD_COLORS.high,
        'extreme', HAZARD_COLORS.extreme,
        '#6b7280', // fallback: gray for unknown levels
      ],
      'fill-opacity': DEFAULT_FIRE_HAZARD_OPACITY,
    },
  }, beforeId);

  // Outline layer — data-driven line color, thin
  map.addLayer({
    id: 'fire-hazard-outline',
    type: 'line',
    source: FIRE_HAZARD_SOURCE_ID,
    paint: {
      'line-color': [
        'match',
        ['get', 'hazardLevel'],
        'low', HAZARD_OUTLINE_COLORS.low,
        'medium', HAZARD_OUTLINE_COLORS.medium,
        'high', HAZARD_OUTLINE_COLORS.high,
        'extreme', HAZARD_OUTLINE_COLORS.extreme,
        '#4b5563',
      ],
      'line-width': 1,
      'line-opacity': DEFAULT_FIRE_HAZARD_OUTLINE_OPACITY,
    },
  }, beforeId);
}

/**
 * @process removeFireHazardLayers
 *
 * Steps:
 * 1. Remove each layer if it exists on the map.
 * 2. Remove the source if it exists.
 *
 * Inputs:
 * - map: The Mapbox GL map instance.
 *
 * Outputs:
 * - Layers and source removed from the map (no return value).
 *
 * Failure Handling:
 * - Checks existence before removing — no throw if layers don't exist.
 *
 * Idempotency:
 * - REQUIRED — safe to call even if layers were never added.
 */
export function removeFireHazardLayers(map: mapboxgl.Map): void {
  for (const id of FIRE_HAZARD_LAYER_IDS) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(FIRE_HAZARD_SOURCE_ID)) map.removeSource(FIRE_HAZARD_SOURCE_ID);
}

/**
 * @process reorderFireHazardLayersBeforeId
 *
 * Steps:
 * 1. If a hazard fill/outline layer exists AND `beforeId` refers to a layer
 *    that also exists, move the hazard layer to immediately before it.
 *
 * Inputs:
 * - map: The Mapbox GL map instance.
 * - beforeId: id of the layer the hazard layers should render below (e.g.
 *   a host app's lowest parcel/boundary fill layer).
 *
 * Outputs:
 * - Hazard layer z-order updated in place (no return value).
 *
 * Failure Handling:
 * - No-ops if either the hazard layers or `beforeId` don't exist yet —
 *   safe to call speculatively from either "layers added" code path
 *   without needing to know which one ran first.
 *
 * Idempotency:
 * - REQUIRED — safe to call repeatedly / on every parcel-layer (re)add.
 *   Skips the actual `moveLayer()` call when a hazard layer is already
 *   positioned immediately before `beforeId`, so calling this on every
 *   viewport refresh (as hosts are expected to) doesn't force Mapbox to
 *   re-index its style layer array when the order hasn't changed.
 *
 * Notes:
 * - Closes a latent host-app race: `addFireHazardLayers(map, beforeId)`
 *   can only apply `beforeId` correctly if the host's parcel/boundary
 *   layer already exists AT THE MOMENT hazard layers are added. If a host
 *   creates hazard layers before its own parcel layers (e.g. after a style
 *   swap, where both are recreated from scratch and re-add order isn't
 *   guaranteed), the hazard layers land at the top of the stack with no
 *   `beforeId` to apply. Calling this function once the host's parcel
 *   layer is confirmed to exist re-asserts the correct z-order regardless
 *   of which side won the race.
 */
export function reorderFireHazardLayersBeforeId(map: mapboxgl.Map, beforeId: string): void {
  if (!map.getLayer(beforeId)) return;
  const existingIds = FIRE_HAZARD_LAYER_IDS.filter((id) => map.getLayer(id));
  if (existingIds.length === 0) return;

  // No-op fast path: skip ALL moveLayer calls if the hazard layers are
  // already a contiguous, correctly-ordered block immediately before
  // beforeId — this is the steady-state case on every viewport refresh
  // once the order was correctly established once, and avoids forcing
  // Mapbox to re-index its style layer array on every call.
  const layers = map.getStyle()?.layers ?? [];
  const beforeIndex = layers.findIndex((l) => l.id === beforeId);
  const expectedStart = beforeIndex - existingIds.length;
  const alreadyOrdered = beforeIndex !== -1 && expectedStart >= 0
    && existingIds.every((id, i) => layers[expectedStart + i]?.id === id);
  if (alreadyOrdered) return;

  for (const id of existingIds) {
    map.moveLayer(id, beforeId);
  }
}

/**
 * @process setFireHazardOpacity
 *
 * Steps:
 * 1. If the fill layer exists, set its fill-opacity paint property.
 *
 * Inputs:
 * - map: The Mapbox GL map instance.
 * - opacity: Fill opacity value (0.0 to 1.0).
 *
 * Outputs:
 * - Fill layer opacity updated (no return value).
 *
 * Failure Handling:
 * - Checks layer existence — no throw if layer doesn't exist.
 *
 * Idempotency:
 * - REQUIRED — safe to call repeatedly with the same value.
 */
export function setFireHazardOpacity(map: mapboxgl.Map, opacity: number): void {
  if (map.getLayer('fire-hazard-fill')) {
    map.setPaintProperty('fire-hazard-fill', 'fill-opacity', opacity);
  }
}

/**
 * @process setFireHazardVisibility
 *
 * Steps:
 * 1. Set visibility on all fire hazard layers.
 *
 * Inputs:
 * - map: The Mapbox GL map instance.
 * - visible: Whether the layers should be visible.
 *
 * Outputs:
 * - Layer visibility updated (no return value).
 *
 * Failure Handling:
 * - Checks layer existence — no throw if layers don't exist.
 *
 * Idempotency:
 * - REQUIRED — safe to call repeatedly.
 */
export function setFireHazardVisibility(map: mapboxgl.Map, visible: boolean): void {
  const visibility = visible ? 'visible' : 'none';
  for (const id of FIRE_HAZARD_LAYER_IDS) {
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, 'visibility', visibility);
    }
  }
}

/**
 * @process setFireHazardWuiOnlyFilter
 * Purpose: restrict the hazard zone fill/outline layers to WUI
 *          (Wildland-Urban Interface) features only, for a host app's
 *          "WUI Only" sub-toggle (currently used by mobile-pwa's
 *          LayerManager).
 * Inputs:  map — the Mapbox GL map instance. enabled — true to show only
 *          inWUI:true features, false to clear the filter (show all).
 * Outputs: Layer filter updated (no return value).
 * Failure Handling: Checks layer existence — no throw if layers don't exist.
 * Idempotency: REQUIRED — safe to call repeatedly with the same value.
 */
export function setFireHazardWuiOnlyFilter(map: mapboxgl.Map, enabled: boolean): void {
  const filter = enabled ? (['==', ['get', 'inWUI'], true] as unknown as mapboxgl.Expression) : null;
  for (const id of FIRE_HAZARD_LAYER_IDS) {
    if (map.getLayer(id)) {
      map.setFilter(id, filter);
    }
  }
}

/**
 * @process addActiveFireLayer
 * Purpose: add the active-fire perimeter layer — outline only, no fill, so
 *          it never obscures whatever the host app renders underneath.
 * Inputs:  map — the Mapbox GL map instance (its ACTIVE_FIRE_SOURCE_ID
 *          source must already exist).
 *          beforeId — Optional layer ID to insert before.
 * Outputs: Layer registered on the map (no return value).
 * Failure Handling: Mapbox addLayer throws if the layer already exists —
 *          callers should call removeActiveFireLayer() first or check
 *          existence.
 * Idempotency: NOT REQUIRED — calling twice without removing will throw.
 */
export function addActiveFireLayer(map: mapboxgl.Map, beforeId?: string): void {
  map.addLayer({
    id: ACTIVE_FIRE_LAYER_ID,
    type: 'line',
    source: ACTIVE_FIRE_SOURCE_ID,
    paint: {
      'line-color': ACTIVE_FIRE_STYLE.COLOR,
      'line-width': 2.5,
      'line-opacity': ACTIVE_FIRE_STYLE.PULSE_MAX_OPACITY,
    },
  }, beforeId);
}

/**
 * @process removeActiveFireLayer
 * Purpose: remove the active-fire layer and source.
 * Inputs:  map — the Mapbox GL map instance.
 * Outputs: Layer and source removed (no return value).
 * Failure Handling: Checks existence before removing — no throw if absent.
 * Idempotency: REQUIRED — safe to call even if never added.
 */
export function removeActiveFireLayer(map: mapboxgl.Map): void {
  if (map.getLayer(ACTIVE_FIRE_LAYER_ID)) map.removeLayer(ACTIVE_FIRE_LAYER_ID);
  if (map.getSource(ACTIVE_FIRE_SOURCE_ID)) map.removeSource(ACTIVE_FIRE_SOURCE_ID);
}

/**
 * @process setActiveFireVisibility
 * Purpose: toggle the active-fire layer's visibility.
 * Inputs:  map — the Mapbox GL map instance. visible — desired visibility.
 * Outputs: Layer visibility updated (no return value).
 * Failure Handling: Checks layer existence — no throw if absent.
 * Idempotency: REQUIRED — safe to call repeatedly.
 */
export function setActiveFireVisibility(map: mapboxgl.Map, visible: boolean): void {
  if (map.getLayer(ACTIVE_FIRE_LAYER_ID)) {
    map.setLayoutProperty(ACTIVE_FIRE_LAYER_ID, 'visibility', visible ? 'visible' : 'none');
  }
}

/**
 * @process startActiveFirePulse
 * Purpose: start a pulsing opacity animation on the active-fire layer —
 *          Mapbox GL has no native paint-property animation primitive, so
 *          this recomputes line-opacity via a sine wave on an interval, per
 *          this file's Architecture note.
 * Inputs:  map — the Mapbox GL map instance.
 * Outputs: A cleanup function that stops the interval — the caller MUST
 *          call this on unmount/visibility-off to avoid leaking timers and
 *          updating a removed/hidden layer.
 * Failure Handling: Silently no-ops each tick if the layer no longer
 *          exists (e.g. removed mid-animation) — does not throw.
 * Idempotency: NOT REQUIRED — each call starts an independent interval;
 *          callers must not call this twice without stopping the first.
 */
export function startActiveFirePulse(map: mapboxgl.Map): () => void {
  const TICK_MS = 100;
  const startTime = Date.now();
  const { PULSE_MIN_OPACITY, PULSE_MAX_OPACITY, PULSE_PERIOD_MS } = ACTIVE_FIRE_STYLE;
  const amplitude = (PULSE_MAX_OPACITY - PULSE_MIN_OPACITY) / 2;
  const midpoint = PULSE_MIN_OPACITY + amplitude;

  const intervalId = setInterval(() => {
    if (!map.getLayer(ACTIVE_FIRE_LAYER_ID)) return;
    const elapsed = Date.now() - startTime;
    const phase = (elapsed / PULSE_PERIOD_MS) * 2 * Math.PI;
    const opacity = midpoint + amplitude * Math.sin(phase);
    map.setPaintProperty(ACTIVE_FIRE_LAYER_ID, 'line-opacity', opacity);
  }, TICK_MS);

  return () => clearInterval(intervalId);
}
